import React, { useEffect, useState } from 'react';

// Classes & Types
import {
  useGetCurrentUserQuery,
  useGetUserFromUIDQuery,
  useGetUserBlockStateQuery,
  useUpdateBlockStateMutation,
} from '@breakroom-nx/redux';

// Components
import { theme } from '@breakroom-nx/assets';
import { ActivityIndicator } from 'react-native';
import {
  FeedbackRating,
  AnonymousProfile,
  PublicProfie,
  ErrorAndRefetch,
  SecondaryButton,
} from '../..';
import { ScrollView, Center, VStack } from 'native-base';
import { getAuth } from 'firebase/auth';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { EntityBeingReported } from '@breakroom-nx/helpers';

export interface UserProfileProps {
  readonly uid?: string;
}

function UserProfile({ uid }: UserProfileProps) {
  const auth = getAuth();
  const isClient = uid === auth.currentUser?.uid;
  const {
    data: user,
    isLoading,
    isError,
    refetch,
  } = isClient ? useGetCurrentUserQuery() : useGetUserFromUIDQuery(uid);

  const {
    data: blockState,
    isLoading: isLoadingBlockState,
    refetch: refetchBlockState,
  } = useGetUserBlockStateQuery(uid);
  const [setBlockState, mutationDoc] = useUpdateBlockStateMutation();

  const [isBlocking, setBlocking] = useState<boolean>(false); // State to manage blocking animation

  useEffect(() => {
    // console.log('Rendered User Profile');
    return () => {
      // console.log('Unmounted User Profile');
    };
  }, []);

  const blockUser = async () => {
    // console.log(`Blocking User`);
    if (uid) {
      setBlocking(true); // Start the blocking animation
      try {
        await setBlockState(uid);
        await new Promise((resolve) => setTimeout(resolve, 500)); // Ensure animation lasts at least 500ms
        refetchBlockState();
      } catch (error) {
        // console.error('Error blocking user:', error);
      } finally {
        setBlocking(false); // Stop the blocking animation
      }
    }
  };

  if (isLoading) {
    return (
      <VStack height={'full'}>
        <Center flex={1}>
          <ActivityIndicator
            animating={true}
            color={theme.colors.surface[400]}
            hidesWhenStopped={true}
          />
        </Center>
      </VStack>
    );
  }

  if (!user || isError) {
    return (
      <ErrorAndRefetch
        onRefetch={refetch}
        error="Error getting the user, please try again."
      />
    );
  }

  return (
    <ScrollView
      flex={1}
      showsVerticalScrollIndicator={true}
      _contentContainerStyle={{
        pb: 150,
      }}
    >
      {!isClient && user.isAnonymous ? (
        <AnonymousProfile user={user} />
      ) : (
        <PublicProfie user={user} isClient={isClient} isLoading={isLoading} />
      )}

      {!isClient ? (
        <VStack space={5} alignItems={'center'}>
          <FeedbackRating
            uid={user.uid}
            name={user.username}
            entity={EntityBeingReported.User}
          />
          {isBlocking ? ( // Show ActivityIndicator while blocking
            <ActivityIndicator
              animating={true}
              color={theme.colors.surface[400]}
              hidesWhenStopped={true}
            />
          ) : (
            <SecondaryButton
              text={blockState && blockState === true ? 'Unblock' : 'Block'}
              disabled={
                isLoadingBlockState ||
                mutationDoc.status === QueryStatus.pending
              }
              onPress={blockUser}
            />
          )}
        </VStack>
      ) : null}
    </ScrollView>
  );
}

const MemoUserProfile = React.memo(UserProfile);
export { MemoUserProfile };
